import { useTranslation } from "react-i18next";
import { activeFilterChip } from "../../components/common/ActiveFilterBar/ActiveFilterBar.js";
import { messageTypeLabelKey } from "./incoming-report-presentation.js";
import { directionLabelKey } from "./message-log-presentation.js";
import { originLabelKey, platformLabelKey, statusLabelKey } from "./outgoing-report-presentation.js";
import {
  countMessageLogActiveFilters,
  MessageLogFilters,
  type MessageLogFilterValues,
} from "./MessageLogFilters.js";
import {
  hasOutboundOnlyMessageLogFilters,
  messageLogDirectionFilter,
  type MessageLogQuery,
} from "./message-log-query.js";
import { useReportFiltersSheet } from "./ReportFilterSheet.js";
import { ReportToolbarRow } from "./ReportToolbarRow.js";

export function MessageLogToolbar({
  query,
  dateFrom,
  dateTo,
  options,
  filteredTotal,
  filtersActive,
  onSearchChange,
  onDateRangeChange,
  onFiltersChange,
  onResetFilters,
  columnsSlot,
}: {
  query: MessageLogQuery;
  dateFrom: string;
  dateTo: string;
  options: Parameters<typeof MessageLogFilters>[0]["options"];
  filteredTotal: number | null;
  filtersActive: boolean;
  onSearchChange: (value: string) => void;
  onDateRangeChange: (from: string, to: string) => void;
  onFiltersChange: (patch: Partial<MessageLogFilterValues>) => void;
  onResetFilters: () => void;
  columnsSlot?: React.ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { filtersOpen, setFiltersOpen } = useReportFiltersSheet();
  const direction = messageLogDirectionFilter(query);
  const showOutboundOnlyFilters = direction !== "INBOUND";
  const incompatibleOutbound =
    direction === "INBOUND" && hasOutboundOnlyMessageLogFilters(query);
  const filterValues: MessageLogFilterValues = {
    direction: query.direction,
    platform: query.platform,
    endpointId: query.endpointId,
    messageType: query.messageType,
    status: query.status,
    origin: query.origin,
    campaignId: query.campaignId,
    template: query.template,
  };
  const activeCount = countMessageLogActiveFilters(filterValues, showOutboundOnlyFilters);
  const endpoint = options?.endpoints.find((item) => item.endpointId === query.endpointId);
  const campaign = options?.campaigns.find((item) => item.campaignId === query.campaignId);
  const filterChips = [
    activeFilterChip(
      "direction",
      t("reports.messageLog.filters.direction"),
      query.direction ? t(directionLabelKey(query.direction)) : null,
      () => onFiltersChange({ direction: undefined }),
    ),
    activeFilterChip(
      "platform",
      t("reports.messageLog.filters.channel"),
      query.platform ? t(platformLabelKey(query.platform)) : null,
      () => onFiltersChange({ platform: undefined }),
    ),
    activeFilterChip(
      "endpoint",
      t("reports.messageLog.filters.account"),
      endpoint
        ? endpoint.secondaryLabel
          ? `${endpoint.displayLabel} · ${endpoint.secondaryLabel}`
          : endpoint.displayLabel
        : null,
      () => onFiltersChange({ endpointId: undefined }),
    ),
    activeFilterChip(
      "messageType",
      t("reports.messageLog.filters.messageType"),
      query.messageType ? t(messageTypeLabelKey(query.messageType)) : null,
      () => onFiltersChange({ messageType: undefined }),
    ),
    activeFilterChip(
      "status",
      t("reports.messageLog.filters.status"),
      query.status ? t(statusLabelKey(query.status)) : null,
      () => onFiltersChange({ status: undefined }),
    ),
    activeFilterChip(
      "origin",
      t("reports.messageLog.filters.origin"),
      query.origin ? t(originLabelKey(query.origin)) : null,
      () => onFiltersChange({ origin: undefined }),
    ),
    activeFilterChip(
      "campaign",
      t("reports.messageLog.filters.campaign"),
      campaign?.name,
      () => onFiltersChange({ campaignId: undefined }),
    ),
    activeFilterChip(
      "template",
      t("reports.messageLog.filters.template"),
      query.template,
      () => onFiltersChange({ template: undefined }),
    ),
  ].flatMap((chip) => (chip ? [chip] : []));

  const filters = (
    <MessageLogFilters
      options={options}
      values={filterValues}
      onChange={onFiltersChange}
      showOutboundOnlyFilters={showOutboundOnlyFilters}
      idPrefix="reports-message-log-more"
    />
  );

  return (
    <div className="flex shrink-0 flex-col gap-1">
      <ReportToolbarRow
        toolbarTestId="reports-message-log-toolbar"
        search={query.search}
        onSearchChange={onSearchChange}
        searchPlaceholder={t("reports.messageLog.searchPlaceholder")}
        searchTestId="reports-message-log-search"
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateRangeChange={onDateRangeChange}
        fromTestId="reports-message-log-from"
        toTestId="reports-message-log-to"
        columnsSlot={columnsSlot}
        moreFilters={filters}
        moreFiltersCount={activeCount}
        moreFiltersTestId="reports-message-log-more-filters"
        mobileFiltersOpen={filtersOpen}
        onMobileFiltersOpenChange={setFiltersOpen}
        mobileFiltersTitle={t("reports.messageLog.filtersTitle")}
        mobileFiltersDescription={t("reports.messageLog.filtersDescription")}
        mobileFiltersTestId="reports-message-log-filters-sheet"
        mobileFiltersOpenTestId="reports-message-log-open-filters"
        mobileFiltersBody={
          <MessageLogFilters
            options={options}
            values={filterValues}
            onChange={onFiltersChange}
            idPrefix="reports-message-log-sheet"
            showOutboundOnlyFilters={showOutboundOnlyFilters}
          />
        }
        filtersActive={filtersActive}
        explicitDateFrom={query.from}
        explicitDateTo={query.to}
        activeFilters={filterChips}
        onResetFilters={onResetFilters}
        resetTestId="reports-message-log-reset"
        filteredCountSlot={
          filteredTotal != null ? (
            <p
              className="m-0 flex h-8 shrink-0 items-center whitespace-nowrap text-[0.75rem] text-muted"
              data-testid="reports-message-log-filtered-count"
            >
              {t("reports.messageLog.filteredCount", {
                formatted: filteredTotal.toLocaleString(i18n.language),
              })}
            </p>
          ) : null
        }
      />
      {incompatibleOutbound ? (
        <p
          className="m-0 text-[0.75rem] text-amber-700 dark:text-amber-400"
          data-testid="reports-message-log-incompatible-filters"
        >
          {t("reports.messageLog.incompatibleOutboundFilters")}
        </p>
      ) : null}
    </div>
  );
}
